import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Data, RouterLink } from '@angular/router';
import { CollapseMenuComponent, SpinnerComponent, TagComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PaginatorComponent } from '../../snippets/paginator/paginator.component';
import { HeaderTagsComponent } from '../../snippets/header-tags/header-tags.component';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { interval, Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { collapse } from '../../../../assets/animations/animations';
import { CompanyInfo } from '../../../interfaces/company-info';
import { Job } from '../../../interfaces/job';
import { ColorService } from '../../../services/color.service';
import { EmbedService } from '../../../services/embed.service';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { JobsService } from '../../../services/jobs.service';
import { SEOService } from '../../../services/seo.service';

@UntilDestroy()
@Component({
  selector: 'app-jobboard',
  templateUrl: './jobboard.component.html',
  styleUrls: ['./jobboard.component.scss'],
  animations: [collapse],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    TranslocoPipe,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    NgSelectModule,
    CollapseMenuComponent,
    SpinnerComponent,
    TagComponent,
    PaginatorComponent,
    HeaderTagsComponent,
    FaIconComponent
],
})
export class JobboardComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly colorService = inject(ColorService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SEOService);
  private readonly translateService = inject(TranslocoService);
  public readonly embedService = inject(EmbedService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly jobsService = inject(JobsService);

  jobs$: Observable<Array<Job>>;
  companyInfo$: Observable<CompanyInfo>;
  company: number = null;
  filterForm: FormGroup;

  ngOnInit(): void {
    this.company = this.route.snapshot.params['companyId'];

    this.jobsService.setFilterByUrl();
    this.jobsService.showPreselectedFilters();
    this.jobsService.getFilterOptions(this.company);
    this.jobsService.getJobs(null, this.company);

    this.filterForm = this.fb.group({
      search: this.fb.control(this.jobsService.currentFilter.searchQuery, []),
      facilities: this.fb.control(
        this.jobsService.currentFilter.facilities,
        []
      ),
      workingTimes: this.fb.control(
        this.jobsService.currentFilter.workingTimes,
        []
      ),
      employmentTypes: this.fb.control(
        this.jobsService.currentFilter.employmentTypes,
        []
      ),
    });

    this.filterForm.valueChanges
      .pipe(debounceTime(300), untilDestroyed(this))
      .subscribe((value) => {
        this.jobsService.currentFilter.pageIndex = 0;
        this.jobsService.currentFilter.searchQuery = value.search;
        this.jobsService.currentFilter.facilities = value.facilities;
        this.jobsService.currentFilter.workingTimes = value.workingTimes;
        this.jobsService.currentFilter.employmentTypes = value.employmentTypes;
        this.jobsService.appendCurrentFiltersToRoute();
        this.jobsService.getJobs(null, this.company);
      });

    this.companyInfo$ = this.route.data.pipe(
      map((data: Data) => data['companyInfo'])
    );
    this.companyInfo$.subscribe((company: CompanyInfo) => {
      if (company) {
        this.seoService.updateTitle(
          this.translateService.translate('jobboard.views.jobboard.jobs-at', {
            title: company.title,
          })
        );
        this.seoService.updateDescription('');
      } else {
        this.seoService.updateTitle('Jobboard | catchHR');
        this.seoService.updateDescription('');
      }
    });
    this.colorService.setDesign(this.route.snapshot.params['companyId']);

    interval(500)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.cdr.markForCheck());
  }

  toggleFilter() {
    this.jobsService.showFilter.general = !this.jobsService.showFilter.general;
    this.cdr.markForCheck();
  }

  facilitySearchFn(term: string, item) {
    term = term.toLocaleLowerCase();
    return item.city?.toLocaleLowerCase().indexOf(term) > -1;
  }
}
